/**
 * 리메시(감량) — Garland–Heckbert 이차 오차(QEM) 엣지 붕괴.
 *
 * Meshy 의 "리메시" 단계가 하는 일이다: 헐에서 나온 10만 삼각형을 게임이 원하는 3만으로.
 * 삼각형 수는 곧 드로우 비용이고, 리깅 뒤에는 스킨 계산 비용이기도 하다.
 *
 * 붕괴는 언제나 **끝점 중 하나로** 한다(최적 위치 계산 없음). UV·법선·가시성 같은 정점
 * 속성을 그대로 물려받아야 베이크한 텍스처가 안 틀어지기 때문이다. 대신 두 후보 중 오차가
 * 작은 쪽을 고른다. 경계 정점(차트 경계 = 면이 하나뿐인 엣지의 끝점)은 잠근다 — 차트가
 * 찢어지면 텍스처가 샌다. 법선이 뒤집히는 붕괴는 거른다.
 */

import * as THREE from 'three';

export interface DecimateResult {
  geometry: THREE.BufferGeometry;
  triangles: number;
  vertices: number;
}

class MinHeap {
  keys: number[] = [];
  vals: number[] = [];
  push(k: number, v: number) {
    this.keys.push(k); this.vals.push(v);
    let i = this.keys.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.keys[p]! <= this.keys[i]!) break;
      this.swap(i, p); i = p;
    }
  }
  pop(): number {
    const v = this.vals[0]!;
    const lk = this.keys.pop()!, lv = this.vals.pop()!;
    if (this.keys.length) {
      this.keys[0] = lk; this.vals[0] = lv;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1, r = l + 1;
        let m = i;
        if (l < this.keys.length && this.keys[l]! < this.keys[m]!) m = l;
        if (r < this.keys.length && this.keys[r]! < this.keys[m]!) m = r;
        if (m === i) break;
        this.swap(i, m); i = m;
      }
    }
    return v;
  }
  get size() { return this.keys.length; }
  private swap(a: number, b: number) {
    const k = this.keys[a]!; this.keys[a] = this.keys[b]!; this.keys[b] = k;
    const v = this.vals[a]!; this.vals[a] = this.vals[b]!; this.vals[b] = v;
  }
}

/** 4×4 대칭 이차 형식을 10개 값으로. */
function quadricAdd(q: Float64Array, o: number, a: number, b: number, c: number, d: number) {
  q[o] += a * a; q[o + 1] += a * b; q[o + 2] += a * c; q[o + 3] += a * d;
  q[o + 4] += b * b; q[o + 5] += b * c; q[o + 6] += b * d;
  q[o + 7] += c * c; q[o + 8] += c * d; q[o + 9] += d * d;
}
function quadricEval(q: Float64Array, o: number, x: number, y: number, z: number): number {
  return q[o]! * x * x + 2 * q[o + 1]! * x * y + 2 * q[o + 2]! * x * z + 2 * q[o + 3]! * x
    + q[o + 4]! * y * y + 2 * q[o + 5]! * y * z + 2 * q[o + 6]! * y
    + q[o + 7]! * z * z + 2 * q[o + 8]! * z + q[o + 9]!;
}

/**
 * @param targetTriangles 목표 삼각형 수. 경계 잠금 때문에 정확히 못 맞출 수 있다.
 */
export function decimate(geometry: THREE.BufferGeometry, targetTriangles: number): DecimateResult {
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
  const index = geometry.getIndex()!;
  const vcount = pos.count;
  const P = pos.array as Float32Array;
  const tris = new Int32Array(index.array as ArrayLike<number>);
  const triCount = tris.length / 3;
  const alive = new Uint8Array(triCount).fill(1);

  // 정점 → 삼각형 목록
  const vtris: number[][] = Array.from({ length: vcount }, () => []);
  for (let t = 0; t < triCount; t++) for (let k = 0; k < 3; k++) vtris[tris[t * 3 + k]!]!.push(t);

  // 이차 형식 + 경계 판정
  const Q = new Float64Array(vcount * 10);
  const edgeCount = new Map<number, number>();
  const ekey = (a: number, b: number) => (a < b ? a * vcount + b : b * vcount + a);
  for (let t = 0; t < triCount; t++) {
    const a = tris[t * 3]!, b = tris[t * 3 + 1]!, c = tris[t * 3 + 2]!;
    const ax = P[a * 3]!, ay = P[a * 3 + 1]!, az = P[a * 3 + 2]!;
    const ux = P[b * 3]! - ax, uy = P[b * 3 + 1]! - ay, uz = P[b * 3 + 2]! - az;
    const vx = P[c * 3]! - ax, vy = P[c * 3 + 1]! - ay, vz = P[c * 3 + 2]! - az;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1e-12;
    nx /= len; ny /= len; nz /= len;
    const d = -(nx * ax + ny * ay + nz * az);
    quadricAdd(Q, a * 10, nx, ny, nz, d); quadricAdd(Q, b * 10, nx, ny, nz, d); quadricAdd(Q, c * 10, nx, ny, nz, d);
    edgeCount.set(ekey(a, b), (edgeCount.get(ekey(a, b)) ?? 0) + 1);
    edgeCount.set(ekey(b, c), (edgeCount.get(ekey(b, c)) ?? 0) + 1);
    edgeCount.set(ekey(c, a), (edgeCount.get(ekey(c, a)) ?? 0) + 1);
  }
  const locked = new Uint8Array(vcount);
  for (const [k, c] of edgeCount) {
    if (c === 1) { const a = Math.floor(k / vcount), b = k % vcount; locked[a] = 1; locked[b] = 1; }
  }

  // 엣지 힙. 값 = a*vcount+b (a<b). 붕괴 후 무효 엣지는 꺼낼 때 검증한다.
  const heap = new MinHeap();
  const cost = (a: number, b: number): [number, number] => {
    // b 를 a 로 보내는 비용 / a 를 b 로 보내는 비용 중 작은 쪽. [비용, 살아남는 정점]
    const qa = quadricEval(Q, a * 10, P[b * 3]!, P[b * 3 + 1]!, P[b * 3 + 2]!) + quadricEval(Q, b * 10, P[b * 3]!, P[b * 3 + 1]!, P[b * 3 + 2]!);
    const qb = quadricEval(Q, a * 10, P[a * 3]!, P[a * 3 + 1]!, P[a * 3 + 2]!) + quadricEval(Q, b * 10, P[a * 3]!, P[a * 3 + 1]!, P[a * 3 + 2]!);
    return qa <= qb ? [qa, b] : [qb, a];
  };
  const pushEdge = (a: number, b: number) => {
    if (a === b) return;
    if (locked[a] && locked[b]) return;
    const [c] = cost(a, b);
    heap.push(c, ekey(a, b));
  };
  for (const k of edgeCount.keys()) pushEdge(Math.floor(k / vcount), k % vcount);

  const removed = new Uint8Array(vcount);
  let live = triCount;
  const target = Math.max(4, Math.floor(targetTriangles));

  const wouldFlip = (v: number, keep: number): boolean => {
    for (const t of vtris[v]!) {
      if (!alive[t]) continue;
      const i0 = tris[t * 3]!, i1 = tris[t * 3 + 1]!, i2 = tris[t * 3 + 2]!;
      if (i0 === keep || i1 === keep || i2 === keep) continue; // 사라질 삼각형
      const r = (i: number) => (i === v ? keep : i);
      const a = i0, b = i1, c = i2, a2 = r(i0), b2 = r(i1), c2 = r(i2);
      const n1 = normalOf(a, b, c), n2 = normalOf(a2, b2, c2);
      if (n1[0] * n2[0] + n1[1] * n2[1] + n1[2] * n2[2] < 0.2) return true;
    }
    return false;
  };
  const normalOf = (a: number, b: number, c: number): [number, number, number] => {
    const ax = P[a * 3]!, ay = P[a * 3 + 1]!, az = P[a * 3 + 2]!;
    const ux = P[b * 3]! - ax, uy = P[b * 3 + 1]! - ay, uz = P[b * 3 + 2]! - az;
    const vx = P[c * 3]! - ax, vy = P[c * 3 + 1]! - ay, vz = P[c * 3 + 2]! - az;
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz) || 1e-12;
    return [nx / l, ny / l, nz / l];
  };

  while (live > target && heap.size) {
    const key = heap.pop();
    const a = Math.floor(key / vcount), b = key % vcount;
    if (removed[a] || removed[b]) continue;
    // 아직 인접한가 (붕괴로 끊긴 엣지일 수 있다)
    let adjacent = false;
    for (const t of vtris[a]!) { if (!alive[t]) continue; if (tris[t * 3] === b || tris[t * 3 + 1] === b || tris[t * 3 + 2] === b) { adjacent = true; break; } }
    if (!adjacent) continue;
    const [, keep] = cost(a, b);
    const gone = keep === a ? b : a;
    // 잠긴 쪽으로만 무너뜨린다.
    if (locked[gone]) continue;
    if (wouldFlip(gone, keep)) continue;

    // 붕괴: gone 의 삼각형을 keep 으로 바꾼다. keep 과 gone 을 같이 가진 삼각형은 죽는다.
    for (const t of vtris[gone]!) {
      if (!alive[t]) continue;
      const i0 = tris[t * 3]!, i1 = tris[t * 3 + 1]!, i2 = tris[t * 3 + 2]!;
      if (i0 === keep || i1 === keep || i2 === keep) { alive[t] = 0; live--; continue; }
      for (let k = 0; k < 3; k++) if (tris[t * 3 + k] === gone) tris[t * 3 + k] = keep;
      vtris[keep]!.push(t);
    }
    removed[gone] = 1;
    for (let i = 0; i < 10; i++) Q[keep * 10 + i]! += Q[gone * 10 + i]!;
    // keep 주변 엣지 다시 넣기
    const seen = new Set<number>();
    for (const t of vtris[keep]!) {
      if (!alive[t]) continue;
      for (let k = 0; k < 3; k++) { const v = tris[t * 3 + k]!; if (v !== keep && !seen.has(v)) { seen.add(v); pushEdge(keep, v); } }
    }
  }

  // 압축
  const remap = new Int32Array(vcount).fill(-1);
  const outIndex: number[] = [];
  let nv = 0;
  for (let t = 0; t < triCount; t++) {
    if (!alive[t]) continue;
    for (let k = 0; k < 3; k++) {
      const v = tris[t * 3 + k]!;
      if (remap[v] < 0) remap[v] = nv++;
      outIndex.push(remap[v]!);
    }
  }
  const g = new THREE.BufferGeometry();
  for (const name of Object.keys(geometry.attributes)) {
    const src = geometry.getAttribute(name) as THREE.BufferAttribute;
    const item = src.itemSize;
    const arr = new Float32Array(nv * item);
    for (let v = 0; v < vcount; v++) {
      const r = remap[v]!;
      if (r < 0) continue;
      for (let k = 0; k < item; k++) arr[r * item + k] = src.getComponent(v, k);
    }
    g.setAttribute(name, new THREE.Float32BufferAttribute(arr, item));
  }
  g.setIndex(outIndex);
  g.computeBoundingBox();
  g.computeBoundingSphere();
  return { geometry: g, triangles: outIndex.length / 3, vertices: nv };
}
